/* global document */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection, indexById } from '../../runtime/mobile/useCollection';
import { sendTestWorkerCommand, TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { confirmDestructive, notify, promptText } from '../../ui/dialogs';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Card, ErrorBanner, Field, GhostButton, Loader, Pill, PrimaryButton, SectionLabel, TextButton, screen } from '../ui';

const DOW = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const MON = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const toMs = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : v instanceof Date ? v.getTime() : Date.parse(v));
const hhmm = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const dLabel = (d) => `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
const locEmoji = (loc) => {
  const s = `${loc?.id || ''} ${loc?.name || ''}`.toLowerCase();
  if (/таб|tab|smoke|сигар|вейп|vape|снюс/.test(s)) return '🚬';
  if (/коф|cofe|coff|бариста|кафе|latte|эспрес/.test(s)) return '☕';
  return '🏪';
};
const HHMM_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

export default function MyScheduleScreen() {
  const { user, profile } = useAuth();
  const active = profile?.active === true;
  const schedules = useCollection('scheduleEntries', { enabled: active, filters: [{ field: 'originalEmployeeId', op: '==', value: user?.uid || '_' }] });
  const takenSchedules = useCollection('scheduleEntries', { enabled: active, filters: [{ field: 'currentEmployeeId', op: '==', value: user?.uid || '_' }] });
  const locations = useCollection('locations', { enabled: active });

  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [repl, setRepl] = useState({ mine: [], forMe: [] });

  const loadRepl = useCallback(async () => {
    if (!active || !user) return;
    try {
      const token = await user.getIdToken();
      const r = await fetch(`${TEST_WORKER_URL}/replacement/list`, {
        method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: '{}',
      });
      const j = await r.json();
      if (j && (j.mine || j.forMe)) setRepl({ mine: j.mine || [], forMe: j.forMe || [] });
    } catch { /* keep */ }
  }, [active, user]);
  useEffect(() => {
    loadRepl();
    const id = setInterval(() => { if (typeof document === 'undefined' || !document.hidden) loadRepl(); }, 20000);
    return () => clearInterval(id);
  }, [loadRepl]);

  const seekReplacement = (entry) => {
    promptText('Причина (по желанию)', '', async (reason) => {
      setBusyId(entry.id); setError(null);
      try {
        const token = await user.getIdToken();
        const r = await fetch(`${TEST_WORKER_URL}/replacement/request`, {
          method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: JSON.stringify({ scheduleEntryId: entry.id, reason: reason || '' }),
        });
        const j = await r.json();
        if (!r.ok) notify('Не вышло', j.message || j.error || 'Ошибка');
        else { notify('Ищем замену', 'Коллегам с этой точки ушло уведомление.'); loadRepl(); }
      } catch { setError('Нет связи'); }
      setBusyId(null);
    }, { allowEmpty: true });
  };
  const replAction = async (path, requestId) => {
    setBusyId(requestId); setError(null);
    try {
      const token = await user.getIdToken();
      const r = await fetch(`${TEST_WORKER_URL}${path}`, {
        method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ requestId }),
      });
      const j = await r.json();
      if (!r.ok) notify('Не вышло', j.message || j.error || 'Ошибка');
      else loadRepl();
    } catch { setError('Нет связи'); }
    setBusyId(null);
  };

  const locById = indexById(locations.docs);
  const myLocations = useMemo(
    () => locations.docs.filter((l) => l.active && (profile?.locationIds || []).includes(l.id)),
    [locations.docs, profile],
  );

  const upcoming = useMemo(() => {
    const min = startOfToday().getTime();
    const byId = new Map();
    for (const e of [...schedules.docs, ...takenSchedules.docs]) {
      if (e.status === 'cancelled' || toMs(e.plannedStartAt) < min) continue;
      byId.set(e.id, e);
    }
    return [...byId.values()].sort((a, b) => toMs(a.plannedStartAt) - toMs(b.plannedStartAt));
  }, [schedules.docs, takenSchedules.docs]);
  const replByEntry = useMemo(() => {
    const m = {};
    for (const r of repl.mine) m[r.scheduleEntryId] = r;
    return m;
  }, [repl.mine]);

  const withdraw = (entry) => {
    confirmDestructive('Снять смену?', 'Смена уберётся из графика.', 'Снять', async () => {
      setBusyId(entry.id);
      setError(null);
      try {
        await sendTestWorkerCommand({ user, path: '/commands/schedule-withdraw', body: { id: entry.id }, idempotencyKey: `emp:${user.uid}:withdraw:${entry.id}:${Date.now()}` });
      } catch (e) {
        setError(readableMobileError(e));
      } finally {
        setBusyId(null);
      }
    });
  };

  if (!profile) return <Loader />;

  if (!active) {
    return (
      <View style={screen.root}>
        <ScrollView contentContainerStyle={screen.content}>
          <Text style={styles.h}>Мой график</Text>
          <Card><Text style={styles.sub}>Аккаунт на модерации ⏳. Как админ включит доступ — сможешь записываться на смены.</Text></Card>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <Text style={styles.h}>Мой график</Text>
        <ErrorBanner message={error || schedules.error} />
        <PrimaryButton title="＋ Записаться на смену" onPress={() => setAdding(true)} />

        {repl.forMe.length > 0 && (
          <>
            <SectionLabel>Коллегам нужна замена</SectionLabel>
            {repl.forMe.map((r) => (
              <Card key={r.id} style={styles.row}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.rowTitle}>{r.fromName} · {dLabel(new Date(toMs(r.plannedStartAt)))}</Text>
                  <Text style={styles.rowMeta}>
                    {locEmoji(locById[r.locationId])} {hhmm(toMs(r.plannedStartAt))}–{hhmm(toMs(r.plannedEndAt))} · {locById[r.locationId]?.name || r.locationId}
                    {r.reason ? ` · ${r.reason}` : ''}
                  </Text>
                </View>
                <PrimaryButton title="Взять" loading={busyId === r.id} onPress={() => replAction('/replacement/accept', r.id)} />
              </Card>
            ))}
          </>
        )}

        <SectionLabel>Ближайшие смены</SectionLabel>
        {!upcoming.length && <Text style={styles.sub}>Смен пока нет. Запишись на первую 👆</Text>}
        {upcoming.map((e) => {
          const started = e.runtimePrepared;
          const mine = e.createdVia === 'self';
          const isCurrent = e.currentEmployeeId === user.uid;
          const took = isCurrent && e.originalEmployeeId !== user.uid;
          const seeking = replByEntry[e.id];
          return (
            <Card key={e.id} style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.rowTitle}>{dLabel(new Date(toMs(e.plannedStartAt)))}{took ? ' · замена' : ''}</Text>
                <Text style={styles.rowMeta}>
                  {locEmoji(locById[e.locationId])} {hhmm(toMs(e.plannedStartAt))}–{hhmm(toMs(e.plannedEndAt))} · {locById[e.locationId]?.name || e.locationId}
                  {e.position ? ` · ${e.position}` : ''}
                </Text>
                {isCurrent && !started && !seeking && (
                  <Pressable onPress={() => seekReplacement(e)} hitSlop={6}>
                    <Text style={styles.replLink}>Не смогу выйти — нужна замена</Text>
                  </Pressable>
                )}
                {seeking && (
                  <View style={styles.seekingRow}>
                    <Text style={styles.seekingText}>Ищем замену…</Text>
                    <TextButton title="Отменить" kind="muted" onPress={() => replAction('/replacement/cancel', seeking.id)} />
                  </View>
                )}
              </View>
              {started
                ? <Pill label="идёт" kind="good" />
                : mine && !took
                  ? <TextButton title="Снять" kind="danger" onPress={() => withdraw(e)} />
                  : <Pill label={took ? 'взял(а)' : 'от админа'} kind={took ? 'good' : 'muted'} />}
            </Card>
          );
        })}
      </ScrollView>

      <Modal visible={adding} animationType="slide" transparent onRequestClose={() => setAdding(false)}>
        <SignupSheet
          user={user}
          locations={myLocations}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); }}
        />
      </Modal>
    </View>
  );
}

function SignupSheet({ user, locations, onClose, onDone }) {
  const days = useMemo(() => {
    const base = startOfToday();
    return Array.from({ length: 14 }, (_, i) => new Date(base.getTime() + i * 86400000));
  }, []);
  const [dayIdx, setDayIdx] = useState(0);
  const [locationId, setLocationId] = useState(locations[0]?.id || '');
  const [position, setPosition] = useState('');
  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('16:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loc = locations.find((l) => l.id === locationId);
  const templates = loc?.shiftTemplates || [];
  const positions = loc?.positions || [];

  const submit = async () => {
    if (!locationId) { setError('Выбери точку'); return; }
    if (!HHMM_RE.test(start) || !HHMM_RE.test(end)) { setError('Время в формате ЧЧ:ММ'); return; }
    if (positions.length && !position) { setError('Выбери должность'); return; }
    const day = days[dayIdx];
    const mk = (t) => { const [hh, mm] = t.split(':').map(Number); const d = new Date(day); d.setHours(hh, mm, 0, 0); return d; };
    let s = mk(start); let e = mk(end);
    if (e <= s) e = new Date(e.getTime() + 86400000);
    setBusy(true);
    setError(null);
    try {
      await sendTestWorkerCommand({
        user,
        path: '/commands/schedule-signup',
        body: { locationId, position: position || null, plannedStartAt: s.toISOString(), plannedEndAt: e.toISOString() },
        idempotencyKey: `emp:${user.uid}:signup:${locationId}:${s.getTime()}`,
      });
      onDone();
    } catch (err) {
      setError(readableMobileError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.sheet}>
        <View style={styles.sheetHead}>
          <Text style={styles.sheetTitle}>Записаться на смену</Text>
          <TextButton title="Отмена" kind="muted" onPress={onClose} />
        </View>
        <ScrollView contentContainerStyle={{ gap: spacing.md }} keyboardShouldPersistTaps="handled">
          <ErrorBanner message={error} />

          <Text style={styles.pickLabel}>ДЕНЬ</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.dayRow}>
            {days.map((d, i) => (
              <Pressable key={i} onPress={() => setDayIdx(i)} style={[styles.dayChip, i === dayIdx && styles.dayChipOn]}>
                <Text style={[styles.dayChipDow, i === dayIdx && styles.dayChipTextOn]}>{DOW[d.getDay()]}</Text>
                <Text style={[styles.dayChipNum, i === dayIdx && styles.dayChipTextOn]}>{d.getDate()}</Text>
              </Pressable>
            ))}
          </ScrollView>

          <Text style={styles.pickLabel}>ТОЧКА</Text>
          <View style={styles.chips}>
            {locations.map((l) => (
              <Pressable key={l.id} onPress={() => { setLocationId(l.id); setPosition(''); }} style={[styles.chip, locationId === l.id && styles.chipOn]}>
                <Text style={[styles.chipText, locationId === l.id && styles.chipTextOn]}>{l.name}</Text>
              </Pressable>
            ))}
            {!locations.length && <Text style={styles.sub}>Тебя ещё не закрепили ни за одной точкой.</Text>}
          </View>

          {templates.length > 0 && (
            <>
              <Text style={styles.pickLabel}>ШАБЛОН ЧАСОВ</Text>
              <View style={styles.chips}>
                {templates.map((t) => (
                  <Pressable key={t.id || t.label} onPress={() => { setStart(t.start); setEnd(t.end); }}
                    style={[styles.chip, start === t.start && end === t.end && styles.chipOn]}>
                    <Text style={[styles.chipText, start === t.start && end === t.end && styles.chipTextOn]}>{t.label} {t.start}–{t.end}</Text>
                  </Pressable>
                ))}
              </View>
            </>
          )}
          <View style={styles.rowFields}>
            <View style={{ flex: 1 }}><Field label="Начало" value={start} onChangeText={setStart} placeholder="08:00" /></View>
            <View style={{ flex: 1 }}><Field label="Конец" value={end} onChangeText={setEnd} placeholder="16:00" /></View>
          </View>

          {positions.length > 0 && (
            <>
              <Text style={styles.pickLabel}>ДОЛЖНОСТЬ</Text>
              <View style={styles.chips}>
                {positions.map((p) => (
                  <Pressable key={p} onPress={() => setPosition(p)} style={[styles.chip, position === p && styles.chipOn]}>
                    <Text style={[styles.chipText, position === p && styles.chipTextOn]}>{p}</Text>
                  </Pressable>
                ))}
              </View>
            </>
          )}

          <PrimaryButton title="Записаться" onPress={submit} loading={busy} />
          <GhostButton title="Отмена" onPress={onClose} />
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  h: { ...typography.h1, color: colors.textPrimary },
  sub: { ...typography.body, color: colors.textSecondary, marginTop: 2 },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  rowTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14 },
  rowMeta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  replLink: { ...typography.caption, color: colors.primary, fontWeight: '700', marginTop: 6 },
  seekingRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: 4 },
  seekingText: { ...typography.caption, color: colors.warning, fontWeight: '700' },
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '92%' },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  sheetTitle: { ...typography.h3, color: colors.textPrimary },
  pickLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '800', letterSpacing: 1 },
  rowFields: { flexDirection: 'row', gap: spacing.md },
  dayRow: { gap: spacing.sm, paddingVertical: 2 },
  dayChip: { alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: radius.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, minWidth: 46 },
  dayChipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  dayChipDow: { ...typography.caption, color: colors.textMuted, fontSize: 11 },
  dayChipNum: { ...typography.body, color: colors.textSecondary, fontWeight: '800' },
  dayChipTextOn: { color: colors.primary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: { borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  chipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  chipText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  chipTextOn: { color: colors.textPrimary },
});
